import { resolveTerms } from "@/shared/utils/resolveTerms";
import type { SearchHeadlinesParams } from "@/widget-components/newsquawk-headlines/api/types";
import type { SearchIntent } from "./types";
import { dateRangeFor } from "./dateRange";
export { isSearchable } from "./searchable";

/**
 * The search the intent describes.
 *
 * `terms` go through the same resolution the watchlist builder uses, so
 * "Tesla" becomes the TSLA ticker plus its tag rather than a text match on the
 * word — which is the whole reason the model is asked for plain words instead
 * of ids. Anything that doesn't resolve stays as free text, so an unrecognised
 * phrase still searches rather than vanishing.
 */
export async function intentToSearchConfig(
  intent: SearchIntent,
  options: { signal?: AbortSignal; now?: Date } = {}
): Promise<SearchHeadlinesParams> {
  const tickers = [...intent.tickers];
  const keywords: string[] = [];
  const tags: number[] = [];

  if (intent.terms.length > 0) {
    const { resolved } = await resolveTerms(intent.terms, {
      signal: options.signal,
    }).catch(() => ({ resolved: [] }));

    for (const term of resolved) {
      if (term.ticker) tickers.push(term.ticker);
      else if (term.keyword) keywords.push(term.keyword);
      if (term.tagId != null) tags.push(term.tagId);
    }

    // Resolution failed entirely (service down) — search the words themselves
    // rather than returning nothing.
    if (resolved.length === 0) keywords.push(...intent.terms);
  }

  return {
    query: keywords.join(" OR "),
    rows: intent.limit,
    exactMatch: intent.exact_match,
    tags: [...new Set(tags)],
    dateRange: dateRangeFor(intent.date_range, options.now),
    /**
     * Tickers ride in through a synthetic portfolio because that is the only
     * door `transformSearchParams` opens for them — it reads tickers off
     * `portfolios`, not off a top-level field. Marked localOnly so nothing
     * downstream mistakes it for a saved watchlist.
     */
    ...(tickers.length > 0 && {
      portfolios: [
        {
          id: -1,
          name: "Search",
          tickers: [...new Set(tickers)],
          keywords: [],
          color: "#00AEEF",
          priority: 1,
          notifications: {},
          customizations: { includeRelatedHeadlines: false },
          owner_type: "USER" as const,
          localOnly: true,
        },
      ],
    }),
  } as SearchHeadlinesParams;
}
